import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "wall",
    "image",
    "linesCount",
    "thicknessInput",
    "clearSelectButton",
    "linesForm",
    "colorForm"
  ]

  static values = {
    projectId: String,
    floorPlanId: String,
    walls: Array,
    materials: Array,
    selectedWallId: String
  }

  connect() {
    if (!this.hasImageTarget || !this.hasWallTarget) return

    this.materialsMap = new Map((this.materialsValue || []).map((material) => [ String(material.id), material ]))
    this.walls = new Map()
    this.originalWallStates = new Map()
    this.tempWallCounter = 1
    this.mode = this.hasSelectedWallIdValue && this.selectedWallIdValue ? "browse" : "draw"
    this.selectedWallKey = null
    this.hasChanges = false
    this.isPressing = false
    this.isDragging = false
    this.longPressTimer = null
    this.longPressDelay = 200
    this.dragThreshold = 2
    this.startX = null
    this.startY = null
    this.pendingStartPoint = null

    this.loadExistingWalls()
    this.draftWall = this.buildDraftFromForm()

    if (this.hasSelectedWallIdValue && this.selectedWallIdValue) {
      const selectedKey = `existing_${this.selectedWallIdValue}`
      if (this.walls.has(selectedKey)) {
        this.selectedWallKey = selectedKey
        this.mode = "browse"
      }
    }

    const onImageReady = () => {
      this.wall = this.wallTarget
      this.ctx = this.wall.getContext("2d")
      this.resizeCanvas()
      this.renderWalls()
      this.updateLinesInfo()
      this.updateFormForCurrentContext()
      this.updateToolbarState()
      this.updateActionButtons()
    }

    this.boundMouseDown = this.handleMouseDown.bind(this)
    this.boundMouseMove = this.handleMouseMove.bind(this)
    this.boundMouseUp = this.handleMouseUp.bind(this)
    this.boundKeyDown = this.handleKeyDown.bind(this)

    this.wallTarget.addEventListener("mousedown", this.boundMouseDown)
    this.wallTarget.addEventListener("mousemove", this.boundMouseMove)
    this.wallTarget.addEventListener("mouseup", this.boundMouseUp)
    document.addEventListener("keydown", this.boundKeyDown)

    if (this.imageTarget.complete) onImageReady()
    else this.imageTarget.addEventListener("load", onImageReady, { once: true })
  }

  disconnect() {
    this.wallTarget?.removeEventListener("mousedown", this.boundMouseDown)
    this.wallTarget?.removeEventListener("mousemove", this.boundMouseMove)
    this.wallTarget?.removeEventListener("mouseup", this.boundMouseUp)
    document.removeEventListener("keydown", this.boundKeyDown)
    if (this.longPressTimer) clearTimeout(this.longPressTimer)
  }

  setMode(event) {
    const nextMode = event.params.mode
    if (!nextMode || this.mode === nextMode) return

    if (nextMode === "draw") {
      this.captureDraftFromForm()
      this.selectedWallKey = null
    }

    this.mode = nextMode
    this.pendingStartPoint = null
    this.updateToolbarState()
    this.updateFormForCurrentContext()
    this.updateActionButtons()
    this.renderWalls()
  }

  changeMaterial(event) {
    const materialId = event.currentTarget.dataset.materialId || event.currentTarget.id
    const material = this.materialsMap.get(String(materialId))
    if (!material) return

    this.draftWall.wallMaterialId = String(material.id)
    this.draftWall.materialName = material.name
    this.draftWall.color = material.color

    if (this.mode === "draw") {
      this.applyDraftToTempWalls()
      this.setThicknessInputValue(material.thickness)
      this.draftWall.thickness = this.parseThickness(material.thickness)
    } else if (this.selectedWallKey) {
      const wall = this.walls.get(this.selectedWallKey)
      if (wall) {
        wall.wallMaterialId = String(material.id)
        wall.materialName = material.name
        wall.color = material.color
      }
    } else {
      this.setThicknessInputValue(material.thickness)
      this.draftWall.thickness = this.parseThickness(material.thickness)
    }

    this.updateHiddenColor()
    this.updateFormForCurrentContext()
    this.renderWalls()
    this.checkForChanges()
  }

  onThicknessChange(event) {
    const thickness = this.parseThickness(event.target.value)
    this.draftWall.thickness = thickness

    if (this.mode === "draw") {
      this.applyDraftToTempWalls()
    } else if (this.selectedWallKey) {
      const wall = this.walls.get(this.selectedWallKey)
      if (wall) wall.thickness = thickness
    }

    this.checkForChanges()
    this.updateLinesInfo()
    this.renderWalls()
  }

  clearLines(event) {
    event.preventDefault()

    const tempKeys = Array.from(this.walls.entries())
      .filter(([, wall]) => !wall.isExisting)
      .map(([key]) => key)

    if (tempKeys.length === 0) {
      this.showToast("No new walls to clear.", "warning")
      return
    }

    tempKeys.forEach((key) => this.walls.delete(key))
    this.selectedWallKey = null
    this.pendingStartPoint = null
    this.updateLinesInfo()
    this.updateFormForCurrentContext()
    this.updateActionButtons()
    this.renderWalls()
    this.checkForChanges()
    this.showToast("All unsaved walls cleared.", "info")
  }

  deleteSelectedWall(event) {
    event.preventDefault()

    if (!this.selectedWallKey) {
      this.showToast("No wall selected to clear.", "warning")
      return
    }

    const wall = this.walls.get(this.selectedWallKey)
    if (!wall) return

    if (wall.isExisting) {
      wall.markedForDeletion = true
      this.showToast("Wall marked for deletion.", "info")
    } else {
      this.walls.delete(this.selectedWallKey)
      this.showToast("Unsaved wall removed.", "success")
    }

    this.selectedWallKey = null
    this.updateLinesInfo()
    this.updateFormForCurrentContext()
    this.updateActionButtons()
    this.renderWalls()
    this.checkForChanges()
  }

  async saveWalls(event) {
    event.preventDefault()

    if (this.mode === "draw" && this.pendingStartPoint) {
      this.showToast("Finish drawing the current wall before saving.", "warning")
      return
    }

    const payload = this.buildCommitPayload()
    if (payload.creates.length === 0 && payload.updates.length === 0 && payload.deletions.length === 0) {
      this.resetFormChanged()
      window.location.href = this.planRedirectUrl("No changes were made")
      return
    }

    try {
      const token = document.querySelector("meta[name='csrf-token']")?.content
      const response = await fetch(this.batchCommitUrl(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": token
        },
        body: JSON.stringify(payload)
      })

      const data = await response.json().catch(() => ({}))
      if (!response.ok || data.success === false) {
        this.showToast(data.error || "Failed to update walls", "error")
        return
      }

      this.resetFormChanged()
      window.location.href = data.redirect_url || this.planRedirectUrl(data.notice)
    } catch (error) {
      this.showToast(`Failed to update walls: ${error.message}`, "error")
    }
  }

  handleMouseDown(event) {
    if (event.button !== 0) return
    if (this.isIgnoredTarget(event.target)) return

    this.isPressing = true
    this.isDragging = false
    this.dragStartX = event.clientX
    this.dragStartY = event.clientY

    this.longPressTimer = setTimeout(() => {
      if (this.isPressing && !this.isDragging) {
        this.isDragging = true
        this.updateCursor("grabbing")
        this.element.dispatchEvent(new CustomEvent("zoom:startDrag", { detail: { event }, bubbles: true }))
      }
    }, this.longPressDelay)
  }

  handleMouseMove(event) {
    if (!this.isPressing) return
    if (this.isIgnoredTarget(event.target)) {
      this.handleMouseUp(event)
      return
    }

    const dx = event.clientX - this.dragStartX
    const dy = event.clientY - this.dragStartY

    if (!this.isDragging && Math.sqrt(dx * dx + dy * dy) > this.dragThreshold) {
      clearTimeout(this.longPressTimer)
      this.isDragging = true
      this.updateCursor("grabbing")
      this.element.dispatchEvent(new CustomEvent("zoom:startDrag", { detail: { event }, bubbles: true }))
    }

    if (this.isDragging) {
      this.element.dispatchEvent(new CustomEvent("zoom:drag", { detail: { event }, bubbles: true }))
    }
  }

  handleMouseUp(event) {
    clearTimeout(this.longPressTimer)

    const rect = this.wallTarget.getBoundingClientRect()
    const x = typeof event.offsetX === "number" ? event.offsetX : (event.clientX - rect.left)
    const y = typeof event.offsetY === "number" ? event.offsetY : (event.clientY - rect.top)

    if (this.isDragging) {
      this.element.dispatchEvent(new CustomEvent("zoom:stopDrag", { detail: { event }, bubbles: true }))
      this.updateCursor(this.mode === "draw" ? "crosshair" : "default")
    } else if (this.mode === "draw") {
      this.handleDrawClick(x, y)
    } else {
      this.handleBrowseClick(x, y)
    }

    this.isPressing = false
    this.isDragging = false
  }

  handleKeyDown(event) {
    if (event.key === "Delete" && this.mode === "browse" && this.selectedWallKey) {
      this.deleteSelectedWall(event)
    }

    if (event.key === "Escape") {
      this.pendingStartPoint = null
      this.renderWalls()
    }
  }

  handleDrawClick(x, y) {
    if (!this.pendingStartPoint) {
      this.pendingStartPoint = { x, y }
      this.renderWalls()
      this.drawStartPoint(x, y, this.currentDraftColor())
      return
    }

    const distance = Math.sqrt((x - this.pendingStartPoint.x) ** 2 + (y - this.pendingStartPoint.y) ** 2)
    if (distance < 10) {
      this.pendingStartPoint = null
      this.renderWalls()
      return
    }

    this.createTempWall(this.pendingStartPoint.x, this.pendingStartPoint.y, x, y)
    this.pendingStartPoint = null
    this.renderWalls()
    this.updateLinesInfo()
    this.checkForChanges()
  }

  handleBrowseClick(x, y) {
    const wallKey = this.findWallKeyAt(x, y)
    this.selectedWallKey = wallKey
    this.updateFormForCurrentContext()
    this.updateActionButtons()
    this.renderWalls()
  }

  resizeCanvas() {
    const width = this.imageTarget.clientWidth || this.imageTarget.naturalWidth
    const height = this.imageTarget.clientHeight || this.imageTarget.naturalHeight

    this.wallTarget.width = width
    this.wallTarget.height = height
    this.wallTarget.style.width = `${width}px`
    this.wallTarget.style.height = `${height}px`
  }

  renderWalls() {
    if (!this.ctx) return

    this.ctx.clearRect(0, 0, this.wallTarget.width, this.wallTarget.height)

    this.activeWalls().forEach((wall) => {
      const isSelected = this.selectedWallKey === wall.id
      const color = isSelected ? "#3c9f00" : wall.color
      const opacity = wall.isExisting ? 0.6 : 1.0
      this.drawLine(wall.startX, wall.startY, wall.endX, wall.endY, color, isSelected ? 1.0 : opacity)
    })

    if (this.pendingStartPoint) {
      this.drawStartPoint(this.pendingStartPoint.x, this.pendingStartPoint.y, this.currentDraftColor())
    }
  }

  drawLine(startX, startY, endX, endY, color, opacity = 1.0) {
    this.ctx.beginPath()
    this.ctx.moveTo(startX, startY)
    this.ctx.lineTo(endX, endY)
    this.ctx.strokeStyle = color || "#000000"
    this.ctx.lineWidth = 5
    this.ctx.globalAlpha = opacity
    this.ctx.stroke()
    this.ctx.globalAlpha = 1.0
  }

  drawStartPoint(x, y, color) {
    this.ctx.beginPath()
    this.ctx.arc(x, y, 3, 0, 2 * Math.PI)
    this.ctx.fillStyle = color || "#000000"
    this.ctx.fill()
  }

  activeWalls() {
    return Array.from(this.walls.values()).filter((wall) => !wall.markedForDeletion)
  }

  loadExistingWalls() {
    ;(this.wallsValue || []).forEach((rawWall) => {
      const wall = this.normalizeWall(rawWall, true)
      this.walls.set(wall.id, wall)
      this.originalWallStates.set(wall.id, this.snapshotWall(wall))
    })
  }

  normalizeWall(rawWall, isExisting) {
    const wallId = isExisting ? `existing_${rawWall.id}` : `temp_${this.tempWallCounter++}`

    return {
      id: wallId,
      originalId: isExisting ? rawWall.id : null,
      startX: Number(rawWall.startX),
      startY: Number(rawWall.startY),
      endX: Number(rawWall.endX),
      endY: Number(rawWall.endY),
      wallMaterialId: String(rawWall.wallMaterialId || rawWall.wall_material_id || this.draftWall?.wallMaterialId || ""),
      materialName: rawWall.material || this.materialNameFor(rawWall.wallMaterialId || rawWall.wall_material_id),
      color: rawWall.color || this.currentDraftColor(),
      thickness: this.parseThickness(rawWall.thickness),
      measurementSystem: rawWall.measurementSystem || rawWall.measurement_system || "",
      isExisting,
      markedForDeletion: false
    }
  }

  buildDraftFromForm() {
    const materialId = this.selectedMaterialId() || String(this.materialsValue?.[0]?.id || "")
    const material = this.materialsMap.get(String(materialId))
    const thickness = this.parseThickness(this.thicknessInputTarget.value || material?.thickness)

    return {
      wallMaterialId: String(materialId),
      materialName: material?.name || "",
      color: material?.color || "#000000",
      thickness
    }
  }

  captureDraftFromForm() {
    const materialId = this.selectedMaterialId()
    const material = this.materialsMap.get(String(materialId))
    this.draftWall.wallMaterialId = String(materialId || this.draftWall.wallMaterialId || "")
    this.draftWall.materialName = material?.name || this.draftWall.materialName
    this.draftWall.color = material?.color || this.draftWall.color
    this.draftWall.thickness = this.parseThickness(this.thicknessInputTarget.value || this.draftWall.thickness)
  }

  createTempWall(startX, startY, endX, endY) {
    const material = this.materialsMap.get(String(this.draftWall.wallMaterialId))
    const wall = this.normalizeWall({
      startX: Math.round(startX),
      startY: Math.round(startY),
      endX: Math.round(endX),
      endY: Math.round(endY),
      wallMaterialId: this.draftWall.wallMaterialId,
      material: material?.name || this.draftWall.materialName,
      color: material?.color || this.draftWall.color,
      thickness: this.draftWall.thickness,
      measurementSystem: this.currentMeasurementSystem()
    }, false)

    this.walls.set(wall.id, wall)
    this.updateHiddenColor()
    this.showToast("Wall added to the draft set.", "success")
  }

  applyDraftToTempWalls() {
    const material = this.materialsMap.get(String(this.draftWall.wallMaterialId))
    const color = material?.color || this.draftWall.color
    const name = material?.name || this.draftWall.materialName

    this.walls.forEach((wall) => {
      if (wall.isExisting || wall.markedForDeletion) return
      wall.wallMaterialId = String(this.draftWall.wallMaterialId)
      wall.materialName = name
      wall.color = color
      wall.thickness = this.draftWall.thickness
    })
  }

  updateFormForCurrentContext() {
    if (this.mode === "browse" && this.selectedWallKey && this.walls.has(this.selectedWallKey)) {
      const wall = this.walls.get(this.selectedWallKey)
      this.setMaterialSelection(wall.wallMaterialId, wall.materialName)
      this.setThicknessInputValue(wall.thickness)
      this.updateHiddenColor(wall.color)
      return
    }

    this.setMaterialSelection(this.draftWall.wallMaterialId, this.draftWall.materialName)
    this.setThicknessInputValue(this.draftWall.thickness)
    this.updateHiddenColor(this.draftWall.color)
  }

  updateToolbarState() {
    const cursorIcon = document.getElementById("cursor-action")
    const drawIcon = document.getElementById("draw-wall-action")
    if (!cursorIcon || !drawIcon) return

    cursorIcon.classList.toggle("active", this.mode === "browse")
    cursorIcon.classList.toggle("icon-green", this.mode === "browse")
    drawIcon.classList.toggle("active", this.mode === "draw")
    drawIcon.classList.toggle("icon-green", this.mode === "draw")
    this.updateCursor(this.mode === "draw" ? "crosshair" : "default")
  }

  updateActionButtons() {
    if (this.hasClearSelectButtonTarget) {
      this.clearSelectButtonTarget.disabled = !this.selectedWallKey
    }
  }

  updateLinesInfo() {
    if (!this.hasLinesCountTarget) return

    const tempCount = Array.from(this.walls.values()).filter((wall) => !wall.isExisting && !wall.markedForDeletion).length
    this.linesCountTarget.textContent = tempCount
  }

  findWallKeyAt(x, y) {
    const walls = this.activeWalls().slice().reverse()
    const wall = walls.find((item) => this.isNearLine(x, y, item))
    return wall?.id || null
  }

  isNearLine(x, y, wall) {
    return this.pointToSegmentDistance(x, y, wall.startX, wall.startY, wall.endX, wall.endY) <= 10
  }

  pointToSegmentDistance(px, py, x1, y1, x2, y2) {
    const a = px - x1
    const b = py - y1
    const c = x2 - x1
    const d = y2 - y1

    const dot = a * c + b * d
    const lenSq = c * c + d * d
    let param = dot / lenSq

    if (param < 0) param = 0
    else if (param > 1) param = 1

    const xx = x1 + param * c
    const yy = y1 + param * d
    const dx = px - xx
    const dy = py - yy

    return Math.sqrt(dx * dx + dy * dy)
  }

  buildCommitPayload() {
    const creates = []
    const updates = []
    const deletions = []

    this.walls.forEach((wall) => {
      if (wall.markedForDeletion) {
        if (wall.isExisting) deletions.push(wall.originalId)
        return
      }

      if (!wall.isExisting) {
        creates.push(this.serializeWallForCommit(wall))
        return
      }

      if (this.hasWallChanged(wall)) {
        updates.push({ id: wall.originalId, ...this.serializeWallForCommit(wall) })
      }
    })

    return { creates, updates, deletions }
  }

  serializeWallForCommit(wall) {
    return {
      start_x: wall.startX,
      start_y: wall.startY,
      end_x: wall.endX,
      end_y: wall.endY,
      wall_material_id: wall.wallMaterialId,
      thickness: wall.thickness
    }
  }

  hasWallChanged(wall) {
    const original = this.originalWallStates.get(wall.id)
    if (!original) return true

    return [ "startX", "startY", "endX", "endY", "wallMaterialId", "thickness" ].some((key) => {
      return String(original[key]) !== String(wall[key])
    })
  }

  snapshotWall(wall) {
    return {
      startX: wall.startX,
      startY: wall.startY,
      endX: wall.endX,
      endY: wall.endY,
      wallMaterialId: String(wall.wallMaterialId),
      thickness: this.parseThickness(wall.thickness)
    }
  }

  checkForChanges() {
    const hasAnyChanges = Array.from(this.walls.values()).some((wall) => {
      if (wall.markedForDeletion) return true
      if (!wall.isExisting) return true
      return this.hasWallChanged(wall)
    })

    if (hasAnyChanges !== this.hasChanges) {
      this.hasChanges = hasAnyChanges
      if (this.hasChanges) this.notifyFormChanged()
      else this.resetFormChanged()
    } else if (this.hasChanges) {
      this.notifyFormChanged()
    }
  }

  notifyFormChanged() {
    document.dispatchEvent(new CustomEvent("formChanged", { detail: { source: "wall-editor" }, bubbles: true }))
  }

  resetFormChanged() {
    document.dispatchEvent(new CustomEvent("formReset", { detail: { source: "wall-editor" }, bubbles: true }))
  }

  selectedMaterialId() {
    const checked = this.element.querySelector("#wall_material_id input[type='radio']:checked")
    return checked?.value || ""
  }

  setMaterialSelection(materialId, fallbackName = "") {
    const radioButtons = this.element.querySelectorAll("#wall_material_id input[type='radio']")
    const value = String(materialId || "")
    radioButtons.forEach((radioButton) => {
      radioButton.checked = radioButton.value === value
    })

    const textTarget = this.element.querySelector("#wall_material_id_dropdown_input_value")
    if (textTarget) {
      const label = this.materialLabelText(value)
      textTarget.textContent = label || fallbackName
    }
  }

  materialLabelText(materialId) {
    const label = this.element.querySelector(`#list_dropdown_${materialId} label.item`)
    return label ? label.textContent.trim() : ""
  }

  setThicknessInputValue(value) {
    if (!this.hasThicknessInputTarget) return
    this.thicknessInputTarget.value = value ?? ""
  }

  updateHiddenColor(color = null) {
    if (!this.hasColorFormTarget) return
    this.colorFormTarget.value = color || this.currentDraftColor()
  }

  currentDraftColor() {
    const material = this.materialsMap.get(String(this.draftWall.wallMaterialId))
    return material?.color || this.draftWall.color || "#000000"
  }

  materialNameFor(materialId) {
    return this.materialsMap.get(String(materialId))?.name || ""
  }

  currentMeasurementSystem() {
    const measurementInput = this.element.querySelector("input[name='wall[wall_scales_attributes][0][measurement_system]']")
    return measurementInput?.value || ""
  }

  parseThickness(value) {
    if (value === "" || value === null || value === undefined) return ""
    const numericValue = Number(value)
    return Number.isFinite(numericValue) ? numericValue : ""
  }

  isIgnoredTarget(target) {
    return target.closest(".management") ||
      target.closest("#floorplan_body") ||
      target.closest(".management__contain") ||
      target.closest(".management__toolbar") ||
      target.closest("#management_section > .management") ||
      target.closest("input") ||
      target.closest("button") ||
      target.closest("select") ||
      target.closest("details") ||
      target.closest("label.item")
  }

  updateCursor(style) {
    if (this.hasWallTarget) this.wallTarget.style.cursor = style
  }

  batchCommitUrl() {
    return `/projects/${this.projectIdValue}/floor_plans/${this.floorPlanIdValue}/walls/batch_commit`
  }

  planRedirectUrl(notice) {
    const baseUrl = `/projects/${this.projectIdValue}/floor_plans/${this.floorPlanIdValue}`
    return notice ? `${baseUrl}?notice=${encodeURIComponent(notice)}` : baseUrl
  }

  showToast(message, type = "success") {
    const toast = document.createElement("div")
    toast.className = "toast-notification"
    toast.textContent = message

    const styles = {
      success: { background: "#64be00", color: "white" },
      warning: { background: "#f8a600", color: "white" },
      error: { background: "#ff2837", color: "white" },
      info: { background: "#1f6feb", color: "white" }
    }
    const style = styles[type] || styles.success

    toast.style.cssText = `
      position: fixed;
      top: 80px;
      right: 20px;
      background: ${style.background};
      color: ${style.color};
      padding: 12px 20px;
      border-radius: 4px;
      z-index: 1000;
      font-size: 14px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    `

    document.body.appendChild(toast)
    setTimeout(() => {
      toast.style.opacity = "0"
      toast.style.transition = "opacity 0.5s"
      setTimeout(() => toast.remove(), 500)
    }, 3000)
  }
}
